// User Guide Page - Refactored to use centralized i18n translations
import { useState, useRef, useCallback } from "react";
import {
  LayoutDashboard, Users, Search, TrendingUp, BarChart3,
  FileText, MessageCircle, GitCompare, Sparkles, Settings,
  LogIn, ChevronDown, ChevronRight, BookOpen, Download,
  Loader2, Zap, FlaskConical, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDashboard } from "@/contexts/DashboardContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { guideTranslations } from "@/i18n/guideTranslations";

// Icon mapping for each section (not translatable)
const SECTION_ICONS = {
  login: LogIn,
  dashboard: LayoutDashboard,
  accounts: Users,
  keywordSearch: Search,
  competitorTracking: TrendingUp,
  followerGrowth: BarChart3,
  contentGrowth: TrendingUp,
  posts: FileText,
  comments: MessageCircle,
  commentSearch: Search,
  crossPlatform: GitCompare,
  aiInsights: Sparkles,
  settings: Settings,
  syncLogs: RefreshCw,
  artistGroups: Users,
  spillover: Zap,
  correlation: FlaskConical,
  reports: FileText,
};

// Section Card Component
const SectionCard = ({ sectionKey, section, icon: Icon, isExpanded, onToggle, proTipLabel }) => (
  <Card className={cn(
    "bg-card border-border/50 transition-all duration-200",
    isExpanded ? "ring-2 ring-primary/30" : "hover:border-primary/30"
  )}>
    <CardHeader
      className="cursor-pointer"
      onClick={onToggle}
      data-testid={`guide-section-${sectionKey}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base">{section.title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{section.description}</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
    </CardHeader>

    {isExpanded && (
      <CardContent className="pt-0">
        <div className="space-y-4">
          <ul className="space-y-2">
            {section.details.map((detail, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <span className="text-primary mt-1">&bull;</span>
                <span>{detail}</span>
              </li>
            ))}
          </ul>

          {section.tips?.length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm font-medium text-amber-600 dark:text-amber-400 flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                {proTipLabel}
              </p>
              {section.tips.map((tip, idx) => (
                <p key={idx} className="text-sm mt-1 text-muted-foreground">{tip}</p>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    )}
  </Card>
);

const GuidePage = () => {
  const { lang } = useDashboard();
  const t = guideTranslations[lang] || guideTranslations.en;
  const [expandedSections, setExpandedSections] = useState(new Set(["dashboard"]));
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const contentRef = useRef(null);

  const sectionKeys = Object.keys(t.sections);

  const toggleSection = (key) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const expandAll = () => setExpandedSections(new Set(sectionKeys));
  const collapseAll = () => setExpandedSections(new Set());

  const downloadPDF = useCallback(async () => {
    if (!contentRef.current) return;

    setIsGeneratingPDF(true);
    const loadingToast = toast.loading(t.generatingPdf);

    try {
      const previousExpanded = new Set(expandedSections);
      setExpandedSections(new Set(sectionKeys));
      await new Promise((r) => setTimeout(r, 500));

      const element = contentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2, useCORS: true, logging: false,
        backgroundColor: "#ffffff", windowWidth: 1200,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;

      const pageHeight = pdfHeight * imgWidth / pdfWidth;
      let heightLeft = imgHeight;

      pdf.addImage(imgData, "PNG", imgX, 0, imgWidth * ratio, imgHeight * ratio);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        const position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", imgX, position * ratio, imgWidth * ratio, imgHeight * ratio);
        heightLeft -= pageHeight;
      }

      const date = new Date().toISOString().split("T")[0];
      const langSuffix = lang === "ko" ? "_한국어" : lang === "zh" ? "_中文" : "_EN";
      pdf.save(`A2O_SNS_Analytics_Guide${langSuffix}_${date}.pdf`);

      setExpandedSections(previousExpanded);
      toast.dismiss(loadingToast);
      toast.success(t.pdfSuccess);
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.dismiss(loadingToast);
      toast.error(t.pdfError);
    } finally {
      setIsGeneratingPDF(false);
    }
  }, [lang, t, sectionKeys, expandedSections]);

  return (
    <div className="space-y-6" data-testid="guide-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" />
            {t.title}
          </h1>
          <p className="text-muted-foreground mt-1">{t.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={expandAll} data-testid="guide-expand-all-btn">
            {t.expandAll}
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} data-testid="guide-collapse-all-btn">
            {t.collapseAll}
          </Button>
          <Button
            size="sm"
            onClick={downloadPDF}
            disabled={isGeneratingPDF}
            className="bg-primary hover:bg-primary/90"
            data-testid="download-pdf-btn"
          >
            {isGeneratingPDF ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {t.downloadPdf}
          </Button>
        </div>
      </div>

      {/* PDF Content Container */}
      <div ref={contentRef} className="space-y-6 bg-background">
        {/* Supported Platforms */}
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg">{t.supportedPlatforms}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {t.platformList.map((platform) => (
                <Badge
                  key={platform.name}
                  variant="outline"
                  className="px-3 py-1.5 text-sm"
                  style={{ borderColor: platform.color, color: platform.color }}
                >
                  {platform.name}
                  <span className="ml-2 text-xs opacity-70">({platform.features})</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Table of Contents */}
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg">{t.tableOfContents}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {sectionKeys.map((key) => {
                const Icon = SECTION_ICONS[key] || FileText;
                return (
                  <Button
                    key={key}
                    variant="ghost"
                    className="justify-start h-auto py-2"
                    onClick={() => {
                      setExpandedSections(new Set([key]));
                      document.getElementById(`section-${key}`)?.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    <Icon className="h-4 w-4 mr-2 text-primary" />
                    <span className="text-sm">{t.sections[key].title}</span>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Feature Sections */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold font-heading">{t.features}</h2>
          <ScrollArea className="h-auto">
            <div className="space-y-3">
              {sectionKeys.map((key) => (
                <div key={key} id={`section-${key}`}>
                  <SectionCard
                    sectionKey={key}
                    section={t.sections[key]}
                    icon={SECTION_ICONS[key] || FileText}
                    isExpanded={expandedSections.has(key)}
                    onToggle={() => toggleSection(key)}
                    proTipLabel={t.proTip}
                  />
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default GuidePage;
